import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocale, useT, withLocale } from '../i18n';
import { api, ApiRequestError, setAccessToken } from '../api/client';

/* Real Client/Partner Portal sign-in — spec §22.1/§17. This replaces the
 * old static site's "Sign in (demo)" no-op button with an actual call
 * to /api/v1/auth/login, including the MFA-challenge step the backend
 * enforces for every admin/client account (spec §25: "MFA required for
 * all administrative or client access in production"). */
export default function PortalLogin() {
  const locale = useLocale();
  const t = useT();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submitLogin(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ mfa_required?: boolean; mfa_token?: string; access_token?: string; user?: unknown }>('/api/v1/auth/login', {
        email,
        password,
      });
      if (res.mfa_required && res.mfa_token) {
        setMfaToken(res.mfa_token);
      } else if (res.access_token) {
        setAccessToken(res.access_token);
        sessionStorage.setItem('cyrkil_access_token', res.access_token);
        navigate(withLocale('/portal', locale));
      }
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Sign-in failed — please try again.');
    } finally {
      setBusy(false);
    }
  }

  async function submitMfa(e: React.FormEvent) {
    e.preventDefault();
    if (!mfaToken) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ access_token: string }>('/api/v1/auth/mfa/verify', { mfa_token: mfaToken, code });
      setAccessToken(res.access_token);
      sessionStorage.setItem('cyrkil_access_token', res.access_token);
      navigate(withLocale('/portal', locale));
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Invalid or expired code.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="section" style={{ paddingTop: 160, minHeight: '60vh' }}>
      <div className="container" style={{ maxWidth: 420 }}>
        <div className="section-head reveal is-visible">
          <span className="eyebrow">{t('portal.eyebrow')}</span>
          <h1>{mfaToken ? 'Verification code' : t('portal.signin_h1')}</h1>
        </div>

        {!mfaToken ? (
          <form onSubmit={submitLogin} className="card reveal is-visible">
            <div className="field">
              <label htmlFor="portalEmail">{t('portal.email_label')}</label>
              <input id="portalEmail" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="portalPassword">{t('portal.password_label')}</label>
              <input id="portalPassword" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
            <p className="body-sm" style={{ marginBottom: 16, opacity: 0.75 }}>
              {t('portal.mfa_note')}
            </p>
            {error && (
              <p className="body-sm" style={{ color: 'var(--status-critical)', marginBottom: 16 }}>
                {error}
              </p>
            )}
            <button type="submit" className="btn btn-primary btn-lg" disabled={busy}>
              {busy ? '…' : t('portal.signin_demo').replace(' (demo)', '')}
            </button>
          </form>
        ) : (
          <form onSubmit={submitMfa} className="card reveal is-visible">
            <div className="field">
              <label htmlFor="mfaCode">6-digit authenticator code</label>
              <input id="mfaCode" required value={code} onChange={(e) => setCode(e.target.value)} autoFocus />
            </div>
            {error && (
              <p className="body-sm" style={{ color: 'var(--status-critical)', marginBottom: 16 }}>
                {error}
              </p>
            )}
            <button type="submit" className="btn btn-primary btn-lg" disabled={busy}>
              {busy ? '…' : 'Verify'}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
